import React from 'react'
import type { RowProps } from '@ztechapps/webui.data-tools'
import type { PivotRowItem } from '../pivot/types'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { PIVOT_TEXT_ALIGN_CLASSES } from '../pivot/constants'
import { formatPivotValue } from '../pivot/formatPivotValue'
import { toPivotRowKey } from '../pivot/pivotRow.utils'
import { PivotRowKindEnum } from '../pivot/PivotRowKindEnum'
import { usePivotGridContext } from './Context'
import styles from './PivotGridRow.module.scss'
import { renderPivotMemberLabel } from './renderPivotMemberLabel'

/** Props for a pivot grid body row. */
export type PivotGridRowProps = (
  | (PivotRowItem & { ariaRowIndex: number; rowIndex: number })
  | { _id: string; spacerHeight: number }
) &
  RowProps

/** Renders a pivot grid body row with its measure cells. */
export const PivotGridRow = (props: PivotGridRowProps) => {
  const { result, leafColumns, isExpanded, onToggle, renderCell, fixedWidths, isCellSelected } = usePivotGridContext()

  if ('spacerHeight' in props) {
    return (
      <tr aria-hidden="true" style={{ height: props.spacerHeight }}>
        <td colSpan={result.rowFields.length + leafColumns.length} className="p-0 border-0" />
      </tr>
    )
  }

  const { kind, path, labelFromLevel, ariaRowIndex, rowIndex } = props
  // collapsed groups aggregate under their own node; anything without a path reads the grand total
  const rowKey = toPivotRowKey(result, props)

  return (
    <tr aria-rowindex={ariaRowIndex} className={kind === PivotRowKindEnum.Subtotal ? 'fw-semibold' : undefined}>
      {result.rowFields.map((field, level) => {
        const node = path[level]
        // levels above the first change repeat the previous row, and levels past a
        // collapsed group's own node have no member — both render empty
        const showLabel = !!node && level >= labelFromLevel
        const expanded = node ? isExpanded(node.key) : false
        // a row-label cell with no label shows nothing to collapse — gating on `showLabel` too
        // keeps the chevron and its click target off every hidden duplicate of the same row.
        const toggleable = !!node && level < result.rowFields.length - 1 && showLabel
        const onLabelClick = (event: React.MouseEvent) => {
          // links and controls inside the label keep their own behaviour
          if ((event.target as HTMLElement).closest('a, button, input')) return
          if (node) onToggle(node.key)
        }
        let content = null
        if (showLabel && node) {
          const isTotal = kind === PivotRowKindEnum.Subtotal && level === path.length - 1
          content = renderPivotMemberLabel({
            renderCell,
            field: field.field,
            label: node.member.label,
            isTotal,
            isBlank: node.member.isBlank,
          })
        }
        // a flex row keeps a block-level `renderCell` result (e.g. a link component) inline next
        // to the chevron instead of wrapping onto its own line and doubling the row height; the
        // content span carries its own `min-width: 0` so it can shrink below its text width and
        // `text-truncate` actually clips it instead of the flex item refusing to shrink
        const label = (
          <HorizontalStack gap={1} className="align-items-baseline">
            <ShowIf condition={toggleable}>
              <BusyButton
                variant="link"
                bsSize="sm"
                className="p-0 lh-1 flex-shrink-0"
                icon={expanded ? 'chevron-down' : 'chevron-right'}
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node?.member.label}`}
                onClick={() => node && onToggle(node.key)}
              />
            </ShowIf>
            <span className={`text-truncate ${styles.flexTruncate}`}>{content}</span>
          </HorizontalStack>
        )
        const labelWidth = fixedWidths[level]
        // `table-active` shades the cell with an inset `box-shadow` of `--bs-table-active-bg`
        // painted over its own background, so the selection reads over `bg-body-tertiary` in
        // light and dark (libs/shipping/webui/src/ShippingPackageEditor/PackageCard.tsx:207-210)
        const selectedClass = isCellSelected(rowIndex, level) ? ' table-active' : ''
        const labelContent =
          labelWidth === undefined ? (
            label
          ) : (
            <div className="d-inline-block align-top" style={{ width: labelWidth }}>
              {label}
            </div>
          )
        return (
          <th
            key={`${field.field}:${level}`}
            scope="row"
            className={`text-nowrap bg-body-tertiary ${
              kind === PivotRowKindEnum.Subtotal ? 'fw-semibold' : 'fw-normal'
            }${selectedClass}`}
            onClick={toggleable ? onLabelClick : undefined}
          >
            {labelContent}
          </th>
        )
      })}
      {leafColumns.map((column, index) => {
        const format = result.measureFormats[column.measureIndex]
        const width = fixedWidths[result.rowFields.length + index]
        const value = formatPivotValue(result.getValue(rowKey, column.columnKey, column.measureIndex), format)
        const selectedClass = isCellSelected(rowIndex, result.rowFields.length + index) ? ' table-active' : ''
        const valueContent =
          width === undefined ? (
            <span>{value}</span>
          ) : (
            <div className="d-inline-block align-top text-truncate" style={{ width }}>
              {value}
            </div>
          )
        return (
          <td key={column.key} className={`${PIVOT_TEXT_ALIGN_CLASSES[format.textAlign]} text-nowrap${selectedClass}`}>
            {valueContent}
          </td>
        )
      })}
    </tr>
  )
}
